'use client'
import { type FC, memo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import type { TypeCmsContentText } from '@/schema'
import { createUnderline, getP18, getP20, getTransition, mq, rgbAlpha, vw } from '@/styles'
import { motion, useAnimation, useInView, type Variants } from 'motion/react'

type Props = {
  data: TypeCmsContentText
}

export type TypeFlexAlign = 'flex-start' | 'center' | 'flex-end'

type DivProps = {
  $alignV: TypeFlexAlign
  $alignH: TypeCmsContentText['align']['h']
  $textPosition: TypeFlexAlign
  $col: TypeCmsContentText['col']
  $color: TypeCmsContentText['color']
}

const getFlexAlignVertical = (textPosition: TypeCmsContentText['align']['v']):TypeFlexAlign => {
  if (!textPosition || textPosition === 'top') return 'flex-start'
  else if (textPosition === 'bottom') return 'flex-end'
  return 'center'
}

export const getFlexAlign = (textPosition:TypeCmsContentText['text_position']):TypeFlexAlign => {
  if (textPosition === 'left') return 'flex-start'
  else if (textPosition === 'right') return 'flex-end'
  return 'center'
}

export const ContentText: FC<Props> = memo(({ data }) => {
  const { align, col, color, text, text_position } = data
  const controls = useAnimation()
  const ref = useRef(null)
  const isInView = useInView(ref, { margin: '0px 0px -100px 0px' })

  useEffect(() => {
    if(isInView) {
      controls.start('animate')
    }
  }, [controls, isInView])

  return (
    <Div
      className='ct ct--text'
      $alignV={getFlexAlignVertical(align.v)}
      $alignH={align.h}
      $textPosition={getFlexAlign(text_position)}
      $col={col}
      $color={color}
      ref={ref}
      variants={titleVariants} {...basicVariantsProps} animate={controls}
    >
      <div dangerouslySetInnerHTML={{ __html: text }} />
    </Div>
  )
})

const Div = styled(motion.div)<DivProps>`
  align-items: ${({ $alignV }) => $alignV};
  display: flex;
  justify-content: center;

  ${mq.greaterThan('tablet')} {
    grid-column: ${({ $col }) => `${$col.from} / span ${$col.expand}`};
    justify-content: ${({ $textPosition }) => $textPosition ?? 'center'};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  a,
  button,
  blockquote,
  dt,
  dd,
  li,
  th,
  td,
  span {
    color: var(--color-text);
    text-align: ${({ $alignH }) => $alignH ?? 'left'};
    word-break: break-word;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    ${createUnderline({ reverse: true })};
    ${getP18()}

  }

  h1, h2, h3, h4, h5, h6,
  p, ul, ol, dl, li, dd,
  q, blockquote {
    margin-bottom: ${vw(15, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(15, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 20px;
    }
  }

  p, ul, ol, dl {
    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(10, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-top: ${vw(10, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(15, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-top: 15px;
        }
      }
    }
  }

  p, ul, ol, dl, li, dd, q, blockquote {
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    padding-left: 18px;
  }

  ul {
    li {
      list-style-type: disc;
    }
  }

  ol {
    li {
      list-style-type: decimal;
    }
  }

  q, blockquote {
    border-left: 0;
    display: block;
    margin: auto;
    text-align: center;
    width: 100%;

    &:last-child {
      margin-bottom: 0;
    }

    > p {
      ${getP20()}
      display: inline;
      font-style: italic;
      font-weight: normal;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      ${getP18()}
      padding: ${vw(20, 'mobile')} ${vw(5, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        padding: ${vw(20, 'tablet')}vw ${vw(5, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        padding: ${vw(30.5, 'desktop')}vw ${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        padding: 30.5px 10px;
      }

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }
    }

    th {
      color: var(--color-text);
      border-bottom: 1px solid ${rgbAlpha('--color-text', .6)};
    }

    tbody, tfoot {
      td {
        border-bottom: 1px solid ${rgbAlpha('--color-text', .1)};
        color: ${rgbAlpha('--color-text', .6)};

        ${mq.greaterThan('tablet')} {
          max-width: ${vw(490, 'desktop')};
          vertical-align: initial;
        }
      }

      tr {
        &:last-child {
          td {
            border-color: ${rgbAlpha('--color-text', 0)};
          }
        }
      }
    }
  }
`


export const basicVariantsProps = {
  initial: 'initial',
  animate: 'animate',
  exit: 'exit',
}

export const titleVariants:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animate: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      duration: .5
    }
  },
  exit: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  }
}